import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { defineMessages, useIntl } from 'react-intl';
import type { VisualDxDrugReactionPayload } from '../../contracts';
import { VisualDxDiagnosisRow } from './VisualDxDiagnosisRow';

/**
 * Heidi-native VisualDx drug-reaction differential (PRD Module 2). Mounted by
 * ``InteractiveWidgetBlock`` for the ``visualdx_drug_ddx`` kind when the LLM
 * calls ``get_drug_reaction_diagnoses`` (drug-first: "what can amoxicillin
 * cause?") and the BE acks with the parsed, capped payload.
 *
 * Rows are ``VisualDxDiagnosisRow`` — the same card as
 * ``VisualDxRankedDifferential`` — so the two lists look identical in the
 * chat. Differences: no rank chip or urgency badge (VisualDx doesn't supply
 * them), and the whole row deep-links to the diagnosis in VisualDx when a
 * URL is present. The match bar is VisualDx's own relative match; like the
 * ranked-differential bar it carries no numeric label so it reads as
 * ordinal, not a calibrated clinical probability (SaMD tone).
 */

const messages = defineMessages({
  emptyForDrug: {
    id: 'b2IofQfx54',
    defaultMessage: 'No drug-reaction diagnoses found for {drug}.',
    description: 'Empty state when VisualDx returns no diagnoses for the named drug',
  },
  empty: {
    id: 'utU0nscm0X',
    defaultMessage: 'No drug-reaction diagnoses found.',
    description:
      'Empty state when VisualDx returns no diagnoses and no drug name is known',
  },
  headingForDrug: {
    id: 'OADaNjUcvq',
    defaultMessage: 'Possible reactions to {drug}',
    description: 'Heading above the list of diagnoses a drug can cause',
  },
  heading: {
    id: 'Om2eHPfCKA',
    defaultMessage: 'Drug-reaction differential',
    description: 'Fallback heading when the drug name is unknown',
  },
  count: {
    id: '5rnrv+pbf8',
    defaultMessage: '{count, plural, one {# diagnosis} other {# diagnoses}}',
    description: 'Number of diagnoses shown in the drug-reaction list',
  },
  rowLabel: {
    id: 'I/Ky2t4nFS',
    defaultMessage: '{name}, open in VisualDx',
    description: 'Accessible label for a diagnosis row that links out to VisualDx',
  },
  disclaimer: {
    id: 'OY4tqMT9ha',
    defaultMessage:
      'VisualDx relative match, not a clinical probability. Images © VisualDx.',
    description: 'Footer under the drug-reaction list',
  },
});

interface VisualDxDrugReactionProps {
  drugReaction: VisualDxDrugReactionPayload;
}

const containerClass =
  'flex w-full flex-col gap-2.5 rounded-xl bg-background-secondary p-4 text-text-primary';

const headerClass = 'flex items-baseline justify-between gap-2 pb-1';

export const VisualDxDrugReaction = ({
  drugReaction,
}: VisualDxDrugReactionProps): JSX.Element => {
  const intl = useIntl();
  const diagnoses = drugReaction.diagnoses ?? [];
  const drug = drugReaction.drug;

  if (diagnoses.length === 0) {
    return (
      <div className={containerClass}>
        <TypographyCaption className="text-text-secondary">
          {drug
            ? intl.formatMessage(messages.emptyForDrug, { drug })
            : intl.formatMessage(messages.empty)}
        </TypographyCaption>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <header className={headerClass}>
        <TypographyP2Bold className="min-w-0 truncate text-text-primary">
          {drug
            ? intl.formatMessage(messages.headingForDrug, { drug })
            : intl.formatMessage(messages.heading)}
        </TypographyP2Bold>
        <TypographyCaption className="shrink-0 text-text-secondary">
          {intl.formatMessage(messages.count, { count: diagnoses.length })}
        </TypographyCaption>
      </header>

      {diagnoses.map((dx, idx) => (
        <VisualDxDiagnosisRow
          key={`${idx}-${dx.name}`}
          name={dx.name}
          imageUrl={dx.thumbnail_url}
          bar={
            dx.match_pct == null
              ? undefined
              : { pct: dx.match_pct, testId: 'visualdx-match-fill' }
          }
          href={dx.visualdx_url}
          ariaLabel={
            dx.visualdx_url
              ? intl.formatMessage(messages.rowLabel, { name: dx.name })
              : dx.name
          }
        />
      ))}

      <TypographyCaption className="text-text-tertiary">
        {intl.formatMessage(messages.disclaimer)}
      </TypographyCaption>
    </div>
  );
};
